import { useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { weekdayName, type ISODate } from '@app/shared'
import clsx from 'clsx'

import { buzz } from '../shell/motion'
import { clockLength } from '../../lib/time'
import { dayDateLine, hm, untilPhrase, type ClassLine, type DotKind, type FreeBlock } from './day'
import { Glyph } from './glyphs'
import { useElapsed, useFitText, useFitWidth, type LiveRecording } from './hooks'

// ============================================================================
// BOARD 01 · THE HERO — the accent card with its bottom-right corner cut away, the status chip in
// the top-right slot, the next-class column bottom-left, and the corner circle that pages between
// the free block and today's progress (owner, 2026-10-08).
//
// THE WHOLE CARD IS A LINK to today's calendar (a push inside the Day tab). The chip sits above it
// and goes where its own state says; the circle sits in the cut-out and is a pager, not a link.
//
// IT IS DRAWN AT THE BOARD'S 362px AND SCALED TO FIT (hooks.ts → useFitWidth): the composition is
// fixed geometry — a 184px column beside a 152px cut-out — and squeezing it would run one into
// the other at the owner's 130% size.
//
// ITS SMALL TEXT IS NOT SCALED (owner A2, 2026-11-04): the date line, the chip, the class line,
// the blocks line and the circle's lines are set at `board px / --m-hero-fit` (hooks.ts publishes
// the fit), so on screen they are board px × the app's zoom — the size the cards below render the
// same board px at. The geometry (weekday, 184px column, 152px cut-out, circle, chip slot) still
// scales with the fit. At 100% on a 390px frame the fit is 1 and nothing changes.
// ============================================================================

export const HERO_WIDTH = 362

export type ChipView =
  | { kind: 'synced'; text: string; stale: boolean; onOpen: () => void }
  | { kind: 'syncing' }
  | { kind: 'recording'; recording: LiveRecording }
  | { kind: 'notification'; count: number; title: string; onOpen: () => void }
  | { kind: 'off' }

export function Hero({
  today,
  classLine,
  dots,
  free,
  progress,
  chip,
}: {
  today: ISODate
  classLine: ClassLine | null
  dots: DotKind[] | null
  /** `error`: the calendar could not be read, so no gap can honestly be claimed. */
  free: FreeBlock | null | 'error'
  /** Done and open on today's plate; null while reminders are unknown. */
  progress: { done: number; open: number } | null
  chip: ChipView
}) {
  const root = useRef<HTMLDivElement>(null)
  const weekday = useRef<HTMLDivElement>(null)
  useFitWidth(root, HERO_WIDTH)
  const day = weekdayName(today)
  useFitText(weekday, 58, day, root)

  return (
    <div ref={root} className="relative h-[292px] w-full flex-none">
      <div className="absolute inset-0 flex flex-col rounded-[36px] bg-m-accent px-[22px] pb-[20px] pt-[22px] text-m-on-accent">
        <Link
          to={`/calendar?date=${today}`}
          aria-label="Open today’s schedule"
          className="absolute inset-0 z-0 rounded-[36px]"
        />
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1 overflow-hidden">
            <div
              ref={weekday}
              className="inline-block whitespace-nowrap text-[58px] font-extrabold uppercase leading-[0.88] tracking-[-0.01em]"
            >
              {day}
            </div>
          </div>
          <StatusChip chip={chip} />
        </div>
        {/* The date spans the card, not the weekday's column: at the cards' size it no longer fits
            beside the chip. */}
        <div className="mt-[6px] whitespace-nowrap text-[length:calc(16px/var(--m-hero-fit,1))] font-semibold">{dayDateLine(today)}</div>

        <div className="mt-auto flex w-[184px] flex-col gap-[10px]">
          {classLine ? <ClassColumn line={classLine} /> : <span className="h-[calc(11px/var(--m-hero-fit,1))]" aria-hidden />}
          {dots && (
            <div className="mt-[2px] flex flex-wrap items-center gap-[6px]">
              {dots.map((d, i) => (
                <span
                  key={i}
                  aria-hidden
                  className={clsx(
                    'box-border flex-none rounded-full',
                    d === 'past' && 'size-[12px] bg-m-on-accent',
                    d === 'current' && 'size-[22px] border-[3px] border-m-on-accent',
                    d === 'upcoming' && 'size-[12px] border-2 border-m-on-accent',
                  )}
                />
              ))}
              <span className={clsx('text-[length:calc(12px/var(--m-hero-fit,1))] font-semibold', dots.length > 0 && 'ml-[4px]')}>
                {dots.length === 0 ? 'Nothing on the calendar' : `${dots.length} ${dots.length === 1 ? 'block' : 'blocks'}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* The cut-out: a ground square with the two concave 36px corners board 01 draws. */}
      {/* 1px past the card on the two outer sides, so a fractional zoom never leaves a hairline of
          accent along the edge. */}
      <div className="absolute -bottom-px -right-px h-[153px] w-[153px] rounded-tl-[36px] bg-m-ground" aria-hidden />
      <div
        className="absolute bottom-0 right-[152px] size-[36px]"
        style={{ background: 'radial-gradient(circle at 0 0, transparent 35.5px, var(--m-ground) 36px)' }}
        aria-hidden
      />
      <div
        className="absolute bottom-[152px] right-0 size-[36px]"
        style={{ background: 'radial-gradient(circle at 0 0, transparent 35.5px, var(--m-ground) 36px)' }}
        aria-hidden
      />
      <CornerCircle free={free} progress={progress} />
    </div>
  )
}

function ClassColumn({ line }: { line: ClassLine }) {
  return (
    <>
      {/* `pointer-events-none`: its opacity gives it a layer above the card's link, so without it
          a tap on the eyebrow would land on nothing instead of opening today's schedule. */}
      <div className="pointer-events-none text-[length:calc(11px/var(--m-hero-fit,1))] font-bold uppercase tracking-[0.12em] opacity-[0.72]">
        {line.eyebrow}
      </div>
      {(line.kind === 'now' || line.kind === 'next') && (
        <div className="flex flex-col gap-[3px]">
          <div className="truncate text-[28px] font-extrabold leading-none tracking-[-0.01em]">{line.code}</div>
          <div className="text-[15px] font-semibold">{line.line}</div>
        </div>
      )}
    </>
  )
}

// ── The status chip ─────────────────────────────────────────────────────────────────────────

// The chip's top sits on the weekday's cap height (owner, 2026-10-07): board 01's 8px at the 58px
// weekday, scaled with the size useFitText settled on (`--m-weekday-size`, set on the hero).
// Its type is at the cards' size (owner A2): 12px / the fit, its 22px height and radius with it,
// and its padding and glyphs in `em` of that, so it keeps board 01's proportions at any fit. Its
// 150px slot stays board geometry: a long title truncates sooner, it never pushes the weekday.
const CHIP =
  'm-chip-in relative z-[2] mt-[calc(var(--m-weekday-size,58px)*8/58)] box-border flex h-[calc(22px/var(--m-hero-fit,1))] max-w-[150px] flex-none items-center rounded-[calc(11px/var(--m-hero-fit,1))] text-[length:calc(12px/var(--m-hero-fit,1))] font-bold leading-none'

function StatusChip({ chip }: { chip: ChipView }) {
  switch (chip.kind) {
    case 'synced':
      return (
        <button
          key="synced"
          type="button"
          onClick={chip.onOpen}
          aria-label={`${chip.text} — open the sync report`}
          className={clsx(
            CHIP,
            'm-tap gap-[0.4167em] px-[0.75em]',
            chip.stale ? 'bg-m-warn-bg text-m-warn-fg' : 'bg-m-hero-wash text-m-on-accent',
          )}
        >
          <Glyph name={chip.stale ? 'warn' : 'check'} size={11} strokeWidth={2.6} className="size-[0.9167em] flex-none" />
          <span className="truncate">{chip.text}</span>
        </button>
      )
    case 'syncing':
      return (
        <span key="syncing" role="status" className={clsx(CHIP, 'gap-[0.4167em] bg-m-hero-wash px-[0.75em] text-m-on-accent')}>
          Syncing…
        </span>
      )
    case 'recording':
      return <RecordingChip key="recording" recording={chip.recording} />
    case 'notification':
      return (
        <button
          key="notification"
          type="button"
          onClick={chip.onOpen}
          aria-label={`${chip.count} new ${chip.count === 1 ? 'notification' : 'notifications'}: ${chip.title}`}
          className={clsx(CHIP, 'm-tap gap-[0.4167em] bg-m-hero-wash pl-[0.3333em] pr-[0.75em] text-m-on-accent')}
        >
          <span className="flex h-[1.3333em] min-w-[1.3333em] flex-none items-center justify-center rounded-[0.6667em] bg-m-hero-pill px-[0.3333em] text-[0.875em] font-extrabold text-m-hero-pill-fg">
            {chip.count}
          </span>
          <span className="truncate">{chip.title}</span>
        </button>
      )
    case 'off':
      return (
        <span
          key="off"
          role="status"
          className={clsx(CHIP, 'gap-[0.5em] bg-m-hero-pill px-[0.75em] text-m-hero-pill-fg')}
        >
          <span className="box-border size-[0.5833em] flex-none rounded-full border-[0.125em] border-m-on-slab-sub" aria-hidden />
          Offline
        </span>
      )
  }
}

/** `REC 23:14`, from the recorder's own start time; `REC` and the course when it gave none. */
function RecordingChip({ recording }: { recording: LiveRecording }) {
  const elapsed = useElapsed(recording.startedAt)
  const what =
    elapsed !== null ? clockLength(elapsed) : (recording.courseCode ?? '')
  return (
    <Link
      to="/school/recordings"
      aria-label={`Recording${recording.courseCode ? ` ${recording.courseCode}` : ''}${elapsed !== null ? `, ${clockLength(elapsed)}` : ''} — open Recordings`}
      className={clsx(CHIP, 'm-tap gap-[0.5em] bg-m-hero-pill pl-[0.5833em] pr-[0.75em] text-m-hero-pill-fg tabular-nums')}
    >
      <span className="m-recdot size-[0.6667em] flex-none rounded-full bg-m-rec-dot" aria-hidden />
      <span className="truncate">REC{what ? ` ${what}` : ''}</span>
    </Link>
  )
}

// ── The corner circle ───────────────────────────────────────────────────────────────────────
//
// Its eyebrow and sub lines are at the cards' size like the rest of the hero's small text. Its
// VALUE lines (the big figure, and the sentences that stand in for it) grow at most 1.15× the
// board (`max(fit, 0.87)`): at the full 1.45× of a 0.69 fit, "1h 19m" is 94% of the 140px circle
// and runs off its curve; at 1.15× it is 75%, inside it.

/** Past this many px (in the circle's own pixels) a release changes page (board 01). */
export const PAGER_THRESHOLD = 28
/** Under this, a release is a tap, and a tap flips the page. */
export const PAGER_TAP = 4

/** The page a release lands on: a drag past the threshold picks a side; a tap flips. */
export function pagerRelease(page: 0 | 1, dx: number): 0 | 1 {
  if (dx < -PAGER_THRESHOLD) return 1
  if (dx > PAGER_THRESHOLD) return 0
  if (Math.abs(dx) < PAGER_TAP) return page === 0 ? 1 : 0
  return page
}

/** Where the track sits for a finger `dx` from where it went down, clamped as board 01 clamps it. */
export function pagerOffset(page: 0 | 1, dx: number): number {
  return Math.max(-150, Math.min(10, -140 * page + dx))
}

function CornerCircle({
  free,
  progress,
}: {
  free: FreeBlock | null | 'error'
  progress: { done: number; open: number } | null
}) {
  const [page, setPage] = useState<0 | 1>(0)
  const track = useRef<HTMLDivElement>(null)
  // POINTER TRACKING LIVES HERE, NOT IN STATE: the move writes the track's transform directly and
  // React hears about the drag once, on release, when the page is decided.
  const drag = useRef<{ id: number; x0: number; scale: number; dx: number } | null>(null)

  const settle = (next: 0 | 1) => {
    const el = track.current
    if (el) {
      el.classList.add('m-snap')
      el.style.transform = `translateX(${-140 * next}px)`
    }
    if (next !== page) buzz(8)
    setPage(next)
  }

  const total = progress ? progress.done + progress.open : 0
  const share = progress && total > 0 ? progress.done / total : 0
  const label =
    page === 0
      ? `${freeLabel(free)}. Swipe for today’s progress`
      : `${progress ? (total === 0 ? 'Nothing due today' : `Today: ${progress.done} of ${total} done`) : 'Today’s progress'}. Swipe for the free block`

  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label={label}
      tabIndex={0}
      data-no-pull=""
      className="absolute bottom-0 right-0 size-[140px] cursor-grab touch-pan-y select-none overflow-hidden rounded-full bg-m-slab"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault()
          settle(e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? 0 : page === 0 ? 1 : 0)
        }
      }}
      onPointerDown={(e) => {
        if (e.button > 0) return
        const rect = e.currentTarget.getBoundingClientRect()
        drag.current = { id: e.pointerId, x0: e.clientX, scale: rect.width > 0 ? rect.width / 140 : 1, dx: 0 }
        try {
          e.currentTarget.setPointerCapture(e.pointerId)
        } catch {
          // A synthetic pointer cannot be captured; the drag still works while over the circle.
        }
        track.current?.classList.remove('m-snap')
      }}
      onPointerMove={(e) => {
        const d = drag.current
        if (!d || d.id !== e.pointerId) return
        d.dx = (e.clientX - d.x0) / d.scale
        if (track.current) track.current.style.transform = `translateX(${pagerOffset(page, d.dx)}px)`
      }}
      onPointerUp={(e) => {
        const d = drag.current
        if (!d || d.id !== e.pointerId) return
        drag.current = null
        settle(pagerRelease(page, d.dx))
      }}
      onPointerCancel={() => {
        drag.current = null
        settle(page)
      }}
    >
      <div
        ref={track}
        className="m-snap flex h-[140px] w-[280px]"
        style={{ transform: `translateX(${-140 * page}px)` }}
      >
        <div className="box-border flex size-[140px] flex-none flex-col items-center justify-center gap-[3px] pb-[6px] text-m-on-slab">
          <FreePage free={free} />
        </div>
        <div
          className="flex size-[140px] flex-none items-center justify-center rounded-full"
          style={{
            background: `conic-gradient(var(--m-accent) 0 ${share * 100}%, var(--m-ring) ${share * 100}% 100%)`,
          }}
        >
          <span className="box-border flex size-[116px] flex-col items-center justify-center gap-[2px] rounded-full bg-m-slab pb-[6px] text-m-on-slab">
            <ProgressPage progress={progress} />
          </span>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-[15px] flex justify-center gap-[4px]" aria-hidden>
        {[0, 1].map((i) => (
          <span
            key={i}
            className={clsx('m-pagerdot h-[5px] rounded-[3px]', page === i ? 'w-[14px] bg-m-accent' : 'w-[5px] bg-m-on-slab-sub')}
          />
        ))}
      </div>
    </div>
  )
}

function freeLabel(free: FreeBlock | null | 'error'): string {
  if (!free) return 'Free block'
  if (free === 'error') return 'Free block: the calendar could not be read'
  if (free.kind === 'gap') return `Free block, ${untilPhrase(free.minutes)}, ${hm(free.start)} to ${hm(free.end)}`
  if (free.kind === 'none') return `No free block until ${hm(free.until)}`
  return 'Free: nothing else on the calendar today'
}

function FreePage({ free }: { free: FreeBlock | null | 'error' }): ReactNode {
  const eyebrow = (
    <span className="text-[length:calc(10.5px/var(--m-hero-fit,1))] font-extrabold uppercase tracking-[0.12em] text-m-on-slab-sub">Free block</span>
  )
  if (!free) return eyebrow
  if (free === 'error') {
    return (
      <>
        {eyebrow}
        <span className="px-4 text-center text-[length:calc(13px/max(var(--m-hero-fit,1),0.87))] font-bold leading-[1.25]">The calendar could not be read</span>
      </>
    )
  }
  if (free.kind === 'gap') {
    const big = untilPhrase(free.minutes)
    return (
      <>
        {eyebrow}
        <span className={clsx('font-extrabold leading-[0.9]', big.length <= 3 ? 'text-[length:calc(46px/max(var(--m-hero-fit,1),0.87))]' : 'text-[length:calc(32px/max(var(--m-hero-fit,1),0.87))]')}>{big}</span>
        <span className="text-[length:calc(12.5px/var(--m-hero-fit,1))] font-bold text-m-accent">
          {hm(free.start)} – {hm(free.end)}
        </span>
      </>
    )
  }
  if (free.kind === 'none') {
    return (
      <>
        <span className="px-3 text-center text-[length:calc(17px/max(var(--m-hero-fit,1),0.87))] font-extrabold leading-[1.05]">No free block</span>
        <span className="text-[length:calc(12.5px/var(--m-hero-fit,1))] font-bold text-m-accent">until {hm(free.until)}</span>
      </>
    )
  }
  return (
    <>
      {eyebrow}
      <span className="text-[length:calc(40px/max(var(--m-hero-fit,1),0.87))] font-extrabold leading-[0.9]">Free</span>
      <span className="text-[length:calc(12.5px/var(--m-hero-fit,1))] font-bold text-m-accent">no more blocks</span>
    </>
  )
}

function ProgressPage({ progress }: { progress: { done: number; open: number } | null }): ReactNode {
  if (!progress) return <span className="text-[length:calc(12px/var(--m-hero-fit,1))] font-semibold text-m-on-slab-sub">…</span>
  const total = progress.done + progress.open
  if (total === 0) {
    return <span className="px-3 text-center text-[length:calc(15px/max(var(--m-hero-fit,1),0.87))] font-extrabold leading-[1.1]">Nothing due today</span>
  }
  return (
    <>
      <span className="text-[length:calc(40px/max(var(--m-hero-fit,1),0.87))] font-extrabold leading-[0.9]">
        {progress.done}
        <span className="text-[length:calc(22px/max(var(--m-hero-fit,1),0.87))] text-m-on-slab-sub">/{total}</span>
      </span>
      <span className="text-[length:calc(12px/var(--m-hero-fit,1))] font-semibold text-m-on-slab-sub">done today</span>
    </>
  )
}
